import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Check, ChevronsUpDown, CalendarIcon, Clock, Tag, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format, addDays, addHours, isAfter } from "date-fns";

interface PreOrderFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menuItem?: {
    id: string;
    name: string;
    price: number;
  };
}

const PreOrderFormModal = ({ open, onOpenChange, menuItem }: PreOrderFormModalProps) => {
  const [user, setUser] = useState<any>(null);
  const [countryCodeOpen, setCountryCodeOpen] = useState(false);
  const [settings, setSettings] = useState({
    maxDays: 7,
    minLeadTimeHours: 24,
  });
  const [formData, setFormData] = useState({
    name: "",
    countryCode: "",
    phone: "",
    address: "",
    quantity: 1,
    notes: "",
    paymentMethod: "cash_on_delivery",
    gcashReference: "",
    paymentProof: null as File | null,
    deliveryDate: undefined as Date | undefined,
    deliveryTime: "",
    referredByPhone: "",
    promoCode: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [promoValidating, setPromoValidating] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<{code: string; discount: number} | null>(null);
  const [isNewCustomer, setIsNewCustomer] = useState(true);
  const { toast } = useToast();

  const countries = [
    { code: "+63", name: "Philippines", flag: "🇵🇭" },
    { code: "+1", name: "United States", flag: "🇺🇸" },
    { code: "+44", name: "United Kingdom", flag: "🇬🇧" },
    { code: "+91", name: "India", flag: "🇮🇳" },
    { code: "+86", name: "China", flag: "🇨🇳" },
    { code: "+81", name: "Japan", flag: "🇯🇵" },
    { code: "+82", name: "South Korea", flag: "🇰🇷" },
    { code: "+65", name: "Singapore", flag: "🇸🇬" },
    { code: "+60", name: "Malaysia", flag: "🇲🇾" },
    { code: "+66", name: "Thailand", flag: "🇹🇭" },
  ];

  const timeSlots = [
    "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM",
    "05:00 PM", "06:00 PM", "07:00 PM", "08:00 PM"
  ];

  // Fetch settings and user profile
  useEffect(() => {
    const loadData = async () => {
      // Fetch order settings
      const { data: settingsData } = await supabase
        .from("settings")
        .select("key, value")
        .in("key", ["pre_order_max_days", "pre_order_lead_time"]);

      if (settingsData) {
        const settingsMap: any = {};
        settingsData.forEach((item) => {
          settingsMap[item.key] = item.value;
        });

        setSettings({
          maxDays: parseInt(settingsMap.pre_order_max_days || "7"),
          minLeadTimeHours: parseInt(settingsMap.pre_order_lead_time || "24"),
        });
      }

      // Fetch user profile
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        
        // Check if user has previous orders (existing customer)
        const { count: orderCount } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', session.user.id);
        
        setIsNewCustomer(orderCount === 0 || orderCount === null);
        
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        
        if (profile) {
          let phoneNumber = profile.phone || "";
          let countryCode = "";
          
          if (phoneNumber.startsWith('+')) {
            const match = phoneNumber.match(/^(\+\d{1,4})(.*)$/);
            if (match) {
              countryCode = match[1];
              phoneNumber = match[2];
            }
          }
          
          setFormData(prev => ({
            ...prev,
            name: profile.full_name || "",
            countryCode: countryCode,
            phone: phoneNumber,
            address: profile.address || ""
          }));
        }
      }
    };
    
    if (open) {
      loadData();
    }
  }, [open]);

  const selectedItem = menuItem || {
    id: "default",
    name: "Hyderabadi Bawarchi Chicken Biryani",
    price: 149
  };

  // Calculate min and max dates for calendar
  const minDate = addHours(new Date(), settings.minLeadTimeHours);
  const maxDate = addDays(new Date(), settings.maxDays);

  // Calculate total with discount
  const subtotal = selectedItem.price * formData.quantity;
  const discount = appliedPromo?.discount || 0;
  const total = Math.max(0, subtotal - discount);

  // Validate promo code
  const validatePromoCode = async () => {
    if (!formData.promoCode.trim()) {
      toast({
        title: "Enter Promo Code",
        description: "Please enter a promo code to apply.",
        variant: "destructive",
      });
      return;
    }

    setPromoValidating(true);
    try {
      const { data: promo, error } = await supabase
        .from('promo_codes')
        .select('*')
        .eq('code', formData.promoCode.trim().toUpperCase())
        .eq('status', 'active')
        .gt('expires_at', new Date().toISOString())
        .single();

      if (error || !promo) {
        toast({
          title: "Invalid Promo Code",
          description: "This promo code is invalid or has expired.",
          variant: "destructive",
        });
        return;
      }

      setAppliedPromo({
        code: promo.code,
        discount: promo.discount_amount
      });
      
      toast({
        title: "Promo Code Applied!",
        description: `₱${promo.discount_amount} discount applied to your order.`,
      });
    } catch (error) {
      console.error('Promo validation error:', error);
      toast({
        title: "Error",
        description: "Failed to validate promo code.",
        variant: "destructive",
      });
    } finally {
      setPromoValidating(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setFormData({ ...formData, promoCode: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to place a pre-order.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.countryCode) {
      toast({
        title: "Country Code Required",
        description: "Please select your country code.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.deliveryDate) {
      toast({
        title: "Delivery Date Required",
        description: "Please select a delivery date.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.deliveryTime) {
      toast({
        title: "Delivery Time Required",
        description: "Please select a delivery time slot.",
        variant: "destructive",
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      let paymentProofUrl = null;
      
      if (formData.paymentMethod === 'gcash' && formData.paymentProof && user) {
        const fileExt = formData.paymentProof.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('payment-proofs')
          .upload(fileName, formData.paymentProof);
        
        if (uploadError) {
          throw new Error(`Failed to upload payment proof: ${uploadError.message}`);
        }
        
        const { data: { publicUrl } } = supabase.storage
          .from('payment-proofs')
          .getPublicUrl(fileName);
        
        paymentProofUrl = publicUrl;
      }

      // Format delivery datetime
      const deliveryDateTime = `${format(formData.deliveryDate, 'yyyy-MM-dd')} ${formData.deliveryTime}`;
      
      const orderDetails = {
        name: formData.name.trim(),
        phone: `${formData.countryCode}${formData.phone.trim()}`,
        address: formData.address.trim(),
        quantity: formData.quantity,
        notes: `PRE-ORDER for ${deliveryDateTime}\n${formData.notes.trim() || ''}`,
        menuItemName: selectedItem.name,
        totalPrice: total,
        paymentMethod: formData.paymentMethod,
        gcashReference: formData.paymentMethod === 'gcash' ? formData.gcashReference.trim() : null,
        paymentProofUrl: paymentProofUrl,
        referredByPhone: formData.referredByPhone.trim() || null,
        promoCode: appliedPromo?.code || null,
        promoDiscount: appliedPromo?.discount || null
      };

      const { data, error } = await supabase.functions.invoke('submit-order', {
        body: orderDetails
      });

      if (error) throw error;

      toast({
        title: "Pre-Order Placed Successfully!",
        description: `Your order is scheduled for ${deliveryDateTime}`,
      });
      
      onOpenChange(false);
      setFormData({ 
        name: "", 
        countryCode: "",
        phone: "", 
        address: "", 
        quantity: 1, 
        notes: "",
        paymentMethod: "cash_on_delivery",
        gcashReference: "",
        paymentProof: null,
        deliveryDate: undefined,
        deliveryTime: "",
        referredByPhone: "",
        promoCode: ""
      });
      setAppliedPromo(null);
      
    } catch (error: any) {
      console.error('Pre-order submission error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit pre-order. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Schedule Pre-Order</DialogTitle>
          <DialogDescription>
            {!user ? (
              "Please sign in to place a pre-order."
            ) : (
              `Schedule your delivery up to ${settings.maxDays} days in advance (minimum ${settings.minLeadTimeHours}h notice)`
            )}
          </DialogDescription>
        </DialogHeader>
        
        {!user ? (
          <div className="space-y-4 py-6">
            <p className="text-center text-muted-foreground">
              You need to be signed in to place pre-orders.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => {
                onOpenChange(false);
                window.location.href = '/customer-auth';
              }} className="w-full">
                Sign In / Sign Up
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  onOpenChange(false);
                  window.open(`https://wa.me/639953947586?text=Hi, I'd like to pre-order ${selectedItem.name}`, '_blank');
                }}
                className="w-full"
              >
                Order via WhatsApp for pre and bulk orders
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 px-1">
            {/* Item Info */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="font-semibold">{selectedItem.name}</p>
              <p className="text-sm text-muted-foreground">₱{selectedItem.price} per order</p>
            </div>

            {/* Delivery Date */}
            <div className="space-y-2">
              <Label htmlFor="deliveryDate">Delivery Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="deliveryDate"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.deliveryDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.deliveryDate ? format(formData.deliveryDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.deliveryDate}
                    onSelect={(date) => setFormData({ ...formData, deliveryDate: date })}
                    disabled={(date) => date < minDate || date > maxDate}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Available from {format(minDate, "MMM dd, yyyy")} to {format(maxDate, "MMM dd, yyyy")}
              </p>
            </div>

            {/* Delivery Time */}
            <div className="space-y-2">
              <Label htmlFor="deliveryTime">Delivery Time Slot *</Label>
              <Select value={formData.deliveryTime} onValueChange={(value) => setFormData({ ...formData, deliveryTime: value })}>
                <SelectTrigger id="deliveryTime">
                  <SelectValue placeholder="Select time slot" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot} value={slot}>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {slot}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                required
                maxLength={100}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <div className="flex gap-2">
                <Popover open={countryCodeOpen} onOpenChange={setCountryCodeOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={countryCodeOpen}
                      className="w-[180px] justify-between"
                    >
                      {formData.countryCode
                        ? countries.find((country) => country.code === formData.countryCode)
                          ? `${countries.find((country) => country.code === formData.countryCode)?.flag} ${formData.countryCode}`
                          : "Select country"
                        : "Select country"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0 bg-background" align="start">
                    <Command>
                      <CommandInput placeholder="Search country..." />
                      <CommandList>
                        <CommandEmpty>No country found.</CommandEmpty>
                        <CommandGroup>
                          {countries.map((country) => (
                            <CommandItem
                              key={country.code}
                              value={`${country.name} ${country.code}`}
                              onSelect={() => {
                                setFormData({ ...formData, countryCode: country.code });
                                setCountryCodeOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.countryCode === country.code ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="mr-2">{country.flag}</span>
                              {country.name} ({country.code})
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Input
                  id="phone"
                  required
                  type="tel"
                  maxLength={15}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                  placeholder="9953947586"
                  className="flex-1"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Delivery Address *</Label>
              <Textarea
                id="address"
                required
                maxLength={500}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter your complete delivery address"
                rows={3}
              />
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                max="100"
                required
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <RadioGroup value={formData.paymentMethod} onValueChange={(value) => setFormData({ ...formData, paymentMethod: value })}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cash_on_delivery" id="cod" />
                  <Label htmlFor="cod" className="font-normal cursor-pointer">Cash on Delivery</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="gcash" id="gcash" />
                  <Label htmlFor="gcash" className="font-normal cursor-pointer">GCash (Pre-payment)</Label>
                </div>
              </RadioGroup>
            </div>

            {/* GCash Details */}
            {formData.paymentMethod === 'gcash' && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                <div className="space-y-2">
                  <Label htmlFor="gcashRef">GCash Reference Number *</Label>
                  <Input
                    id="gcashRef"
                    required
                    maxLength={50}
                    value={formData.gcashReference}
                    onChange={(e) => setFormData({ ...formData, gcashReference: e.target.value })}
                    placeholder="Enter GCash reference number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="paymentProof">Upload Payment Proof *</Label>
                  <Input
                    id="paymentProof"
                    type="file"
                    accept="image/*"
                    required
                    onChange={(e) => setFormData({ ...formData, paymentProof: e.target.files?.[0] || null })}
                  />
                </div>
              </div>
            )}

            {/* Referral Section - Only for new customers */}
            {isNewCustomer && (
              <div className="space-y-2">
                <Label htmlFor="referredByPhone">Referred by (Optional)</Label>
                <Input
                  id="referredByPhone"
                  type="tel"
                  maxLength={20}
                  value={formData.referredByPhone}
                  onChange={(e) => setFormData({ ...formData, referredByPhone: e.target.value.replace(/[^0-9+]/g, '') })}
                  placeholder="e.g., +639171234567"
                />
                <p className="text-xs text-muted-foreground">
                  Enter referrer's phone with country code (e.g., +63 for Philippines). They'll get ₱50 off!
                </p>
              </div>
            )}

            {/* Promo Code Section */}
            <div className="space-y-2">
              <Label htmlFor="promoCode">Promo Code (Optional)</Label>
              {appliedPromo ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <Tag className="h-4 w-4 text-green-600" />
                  <span className="flex-1 text-green-700 dark:text-green-300 font-medium">
                    {appliedPromo.code} - ₱{appliedPromo.discount} off
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removePromo}
                    className="h-8 w-8 p-0 text-green-600 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    id="promoCode"
                    value={formData.promoCode}
                    onChange={(e) => setFormData({ ...formData, promoCode: e.target.value.toUpperCase() })}
                    placeholder="Enter promo code"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={validatePromoCode}
                    disabled={promoValidating}
                  >
                    {promoValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Special Instructions (Optional)</Label>
              <Textarea
                id="notes"
                maxLength={500}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any special requests or instructions?"
                rows={2}
              />
            </div>

            {/* Total */}
            <div className="bg-primary/10 p-4 rounded-lg space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span>Subtotal:</span>
                <span>₱{subtotal.toLocaleString()}</span>
              </div>
              {appliedPromo && (
                <div className="flex justify-between items-center text-sm text-green-600">
                  <span>Promo Discount:</span>
                  <span>-₱{appliedPromo.discount}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-semibold">Total Amount:</span>
                <span className="text-2xl font-bold text-primary">
                  ₱{total.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Submitting..." : "Schedule Pre-Order"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreOrderFormModal;
